import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { softDelete } from '../../../lib/softDelete';
import { supabase } from '../../../lib/supabase';
import type { Database } from '../../../lib/database.types';
import type { CompanyInput, CompanyRow } from './companies.schema';

export const companiesQueryKey = (orgId: string | null) => ['companies', orgId] as const;

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

async function fetchCompanies(orgId: string): Promise<CompanyRow[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('id, name, initials, color, industry, size, website, address, notes, created_at, updated_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('name', { ascending: true });

  if (error) throw error;
  return data;
}

export function useCompanies(orgId: string | null) {
  return useQuery({
    queryKey: companiesQueryKey(orgId),
    queryFn: () => {
      if (!orgId) return Promise.resolve([]);
      return fetchCompanies(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

type CompanyInsert = Database['public']['Tables']['companies']['Insert'];
type CompanyUpdate = Database['public']['Tables']['companies']['Update'];

function toPayload(values: CompanyInput): Omit<CompanyInsert, 'organization_id'> {
  return {
    name: values.name.trim(),
    initials: emptyToNull(values.initials),
    color: values.color,
    industry: emptyToNull(values.industry),
    size: emptyToNull(values.size),
    website: emptyToNull(values.website),
    address: emptyToNull(values.address),
    notes: emptyToNull(values.notes),
  };
}

export interface UpsertCompanyResult {
  id: string;
  created: boolean;
}

export function useUpsertCompany(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: CompanyInput }): Promise<UpsertCompanyResult> => {
      if (!orgId) throw new Error('Missing organization');

      if (id) {
        const payload: CompanyUpdate = toPayload(values);
        const { error } = await supabase.from('companies').update(payload).eq('id', id);
        if (error) throw error;
        return { id, created: false };
      }

      const payload: CompanyInsert = {
        ...toPayload(values),
        organization_id: orgId,
      };
      const { data, error } = await supabase
        .from('companies')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      return { id: data.id, created: true };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesQueryKey(orgId) });
    },
  });
}

// Post-0009: deals are gone. "Company deals" now means the customer's
// projects (which include status='quote' as the pre-acceptance state and
// 'active' as the running state).
export interface CompanyDealRow {
  id: string;
  name: string;
  status: string;
  value_amount: number;
  value_currency: string;
  created_at: string;
}

async function fetchCompanyDeals(orgId: string, companyId: string): Promise<CompanyDealRow[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, status, value_amount, value_currency, created_at')
    .eq('organization_id', orgId)
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export function useCompanyDeals(orgId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: ['company-deals', orgId, companyId] as const,
    queryFn: () => {
      if (!orgId || !companyId) return Promise.resolve<CompanyDealRow[]>([]);
      return fetchCompanyDeals(orgId, companyId);
    },
    enabled: Boolean(orgId && companyId),
    staleTime: 30 * 1000,
  });
}

export interface CompanyActivityRow {
  id: string;
  action: string;
  entity_type: string;
  created_at: string;
}

async function fetchCompanyActivity(orgId: string, companyId: string): Promise<CompanyActivityRow[]> {
  // Pull recent activity on the company itself + its projects and contacts.
  const [projectIdsRes, contactIdsRes] = await Promise.all([
    supabase
      .from('projects')
      .select('id')
      .eq('organization_id', orgId)
      .eq('company_id', companyId),
    supabase
      .from('contacts')
      .select('id')
      .eq('organization_id', orgId)
      .eq('company_id', companyId),
  ]);
  const projectIds = (projectIdsRes.data ?? []).map((r) => r.id);
  const contactIds = (contactIdsRes.data ?? []).map((r) => r.id);

  // Build an OR filter across (companies | projects | contacts) for this customer.
  // RLS still scopes by org. audit_writer writes plural table names as entity_type.
  const orClauses: string[] = [`and(entity_type.eq.companies,entity_id.eq.${companyId})`];
  if (projectIds.length > 0) {
    orClauses.push(`and(entity_type.eq.projects,entity_id.in.(${projectIds.join(',')}))`);
  }
  if (contactIds.length > 0) {
    orClauses.push(`and(entity_type.eq.contacts,entity_id.in.(${contactIds.join(',')}))`);
  }

  const { data, error } = await supabase
    .from('audit_log')
    .select('id, action, entity_type, created_at')
    .eq('organization_id', orgId)
    .or(orClauses.join(','))
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data as CompanyActivityRow[];
}

export function useCompanyActivity(orgId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: ['company-activity', orgId, companyId] as const,
    queryFn: () => {
      if (!orgId || !companyId) return Promise.resolve<CompanyActivityRow[]>([]);
      return fetchCompanyActivity(orgId, companyId);
    },
    enabled: Boolean(orgId && companyId),
    staleTime: 30 * 1000,
  });
}

export function useSoftDeleteCompany(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await softDelete('companies', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesQueryKey(orgId) });
    },
  });
}
